import * as React from "react";
import { Flashbar } from "@amzn/awsui-components-react";
import { getQueryParameterMap, scrollWithDelayInMs } from "../../utils/common-utils";
import {
    parseHighlightParam,
    parseContextParam,
    buildNormalizedToRawMap,
    findAllOccurrences,
    selectOccurrenceByContext
} from "../../utils/highlight-utils";
import "../../styles/component/text-highlight-share.scss";

const HIGHLIGHT_APPLY_DELAY_MS = 1200;

function getContainerElement(containerId: string): HTMLElement | null {
    return document.getElementById(containerId);
}

function getAllTextNodes(root: HTMLElement): Text[] {
    const nodes: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Text | null;
    while ((node = walker.nextNode() as Text)) {
        nodes.push(node);
    }
    return nodes;
}

function buildCharMap(textNodes: Text[]): { accumulated: string; charMap: { nodeIdx: number; offsetInNode: number }[] } {
    let accumulated = "";
    const charMap: { nodeIdx: number; offsetInNode: number }[] = [];

    for (let i = 0; i < textNodes.length; i++) {
        const nodeText = textNodes[i].textContent || "";
        for (let j = 0; j < nodeText.length; j++) {
            charMap.push({ nodeIdx: i, offsetInNode: j });
            accumulated += nodeText[j];
        }
    }

    return { accumulated, charMap };
}

interface HighlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

function findTextRange(containerId: string, param: string, contextParam?: string): Range | null {
    const container = getContainerElement(containerId);
    if (!container) return null;

    const parsed = parseHighlightParam(param);
    const textNodes = getAllTextNodes(container);
    if (textNodes.length === 0) return null;

    const { accumulated, charMap } = buildCharMap(textNodes);
    const normalizedAccumulated = accumulated.replace(/\s+/g, " ");
    const normalizedToRaw = buildNormalizedToRawMap(accumulated);

    let searchNormalized: string;
    if ("exact" in parsed) {
        searchNormalized = parsed.exact.replace(/\s+/g, " ").trim();
    } else {
        const { prefix, suffix } = parsed;
        const normalizedPrefix = prefix.replace(/\s+/g, " ").trim();
        const normalizedSuffix = suffix.replace(/\s+/g, " ").trim();

        const prefixIdx = normalizedAccumulated.indexOf(normalizedPrefix);
        if (prefixIdx === -1) return null;

        const suffixStart = prefixIdx + normalizedPrefix.length;
        const suffixIdx = normalizedAccumulated.indexOf(normalizedSuffix, suffixStart);
        if (suffixIdx === -1) return null;

        searchNormalized = normalizedAccumulated.substring(prefixIdx, suffixIdx + normalizedSuffix.length);
    }

    const occurrences = findAllOccurrences(normalizedAccumulated, searchNormalized);
    if (occurrences.length === 0) return null;

    let chosenIdx: number;
    if (occurrences.length === 1 || !contextParam) {
        chosenIdx = occurrences[0];
    } else {
        const context = parseContextParam(contextParam);
        chosenIdx = selectOccurrenceByContext(normalizedAccumulated, occurrences, searchNormalized.length, context);
    }

    const rawStart = normalizedToRaw[chosenIdx];
    const normalizedEnd = chosenIdx + searchNormalized.length;
    const rawEnd = normalizedEnd < normalizedToRaw.length
        ? normalizedToRaw[normalizedEnd]
        : accumulated.length;

    if (rawStart === undefined || rawEnd === undefined) return null;
    if (rawStart >= charMap.length) return null;

    const startEntry = charMap[rawStart];
    const endRawIdx = Math.min(rawEnd, charMap.length);
    const endEntry = endRawIdx >= charMap.length
        ? { nodeIdx: charMap[charMap.length - 1].nodeIdx, offsetInNode: (textNodes[charMap[charMap.length - 1].nodeIdx].textContent || "").length }
        : charMap[endRawIdx];

    const range = document.createRange();
    range.setStart(textNodes[startEntry.nodeIdx], startEntry.offsetInNode);
    range.setEnd(textNodes[endEntry.nodeIdx], endEntry.offsetInNode);

    return range;
}

function mergeOverlappingRects(rects: HighlightRect[]): HighlightRect[] {
    if (rects.length === 0) return [];
    const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left);
    const merged: HighlightRect[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
        const current = sorted[i];
        const last = merged[merged.length - 1];
        const verticalOverlap = current.top < last.top + last.height * 0.5;
        const horizontalOverlap = current.left < last.left + last.width;

        if (verticalOverlap && horizontalOverlap) {
            const right = Math.max(last.left + last.width, current.left + current.width);
            const bottom = Math.max(last.top + last.height, current.top + current.height);
            last.left = Math.min(last.left, current.left);
            last.top = Math.min(last.top, current.top);
            last.width = right - last.left;
            last.height = bottom - last.top;
        } else {
            merged.push(current);
        }
    }
    return merged;
}

function getHighlightRects(range: Range): HighlightRect[] {
    const clientRects = range.getClientRects();
    const rects: HighlightRect[] = [];
    for (let i = 0; i < clientRects.length; i++) {
        const r = clientRects[i];
        if (r.width > 0 && r.height > 0) {
            rects.push({
                top: r.top + window.scrollY,
                left: r.left + window.scrollX,
                width: r.width,
                height: r.height
            });
        }
    }
    return mergeOverlappingRects(rects);
}

const MAX_RETRIES = 10;
const RETRY_INTERVAL_MS = 500;

export const HighlightHandler = (props: { showNotFoundMessage?: boolean }) => {
    const [showNotFound, setShowNotFound] = React.useState(false);
    const [overlayRects, setOverlayRects] = React.useState<HighlightRect[]>([]);
    const rangeRef = React.useRef<Range | null>(null);

    const updateOverlayPositions = React.useCallback(() => {
        if (!rangeRef.current) return;
        setOverlayRects(getHighlightRects(rangeRef.current));
    }, []);

    React.useEffect(() => {
        if (overlayRects.length === 0) return;
        window.addEventListener("resize", updateOverlayPositions);
        return () => window.removeEventListener("resize", updateOverlayPositions);
    }, [overlayRects.length, updateOverlayPositions]);

    React.useEffect(() => {
        const queryParamMap = getQueryParameterMap();
        const highlightParam = queryParamMap.get("highlight");
        const containerId = queryParamMap.get("container");
        const contextParam = queryParamMap.get("highlightContext");

        if (!highlightParam || !containerId) return;

        let attempt = 0;
        let timer: ReturnType<typeof setTimeout>;

        const tryHighlight = () => {
            attempt++;
            const container = getContainerElement(containerId);
            const hasContent = container && container.textContent && container.textContent.trim().length > 0;

            if (hasContent) {
                const range = findTextRange(containerId, highlightParam, contextParam);
                if (range) {
                    rangeRef.current = range;
                    const rects = getHighlightRects(range);
                    setOverlayRects(rects);

                    if (rects.length > 0) {
                        const scrollTarget = rects[0].top - (window.innerHeight / 3);
                        window.scrollTo({ top: scrollTarget, behavior: "smooth" });
                    }
                    return;
                }
            }

            if (attempt < MAX_RETRIES) {
                timer = setTimeout(tryHighlight, RETRY_INTERVAL_MS);
            } else {
                setShowNotFound(true);
                scrollWithDelayInMs(0, containerId);
            }
        };

        timer = setTimeout(tryHighlight, HIGHLIGHT_APPLY_DELAY_MS);

        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            {overlayRects.length > 0 && overlayRects.map((rect, i) => (
                <div
                    key={i}
                    className="pf-text-highlight-overlay"
                    style={{
                        top: `${rect.top}px`,
                        left: `${rect.left}px`,
                        width: `${rect.width}px`,
                        height: `${rect.height}px`,
                    }}
                />
            ))}
            {showNotFound && props.showNotFoundMessage && (
                <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 10000, maxWidth: 500 }}>
                    <Flashbar items={[{
                        type: "warning",
                        dismissible: true,
                        onDismiss: () => setShowNotFound(false),
                        content: "The highlighted text could not be found. The page content may have been updated since this link was created."
                    }]} />
                </div>
            )}
        </>
    );
};
